import type { PopupArrowMode, PopupArrowPlacement } from '@/shared/types/popup';
import {
  POPOVER_ARROW_MIN_GAP_TO_TRIGGER_PX,
  POPUP_ARROW_OVERHANG_PX,
  resolvePopupArrowPlacement,
} from './popupArrowPlacement';

export type PopupPositionProp =
  | 'top'
  | 'top-left'
  | 'top-right'
  | 'bottom'
  | 'bottom-center'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'right';

/**
 * Acota el puntero al rectángulo del trigger en coordenadas de viewport.
 * Devuelve la esquina superior izquierda de un ancla 1×1 lógica dentro del trigger
 * (recorrido píxel a píxel cuando el trigger tiene tamaño entero en CSS px).
 */
export function clampClientPointForPopupAnchor(
  triggerRect: DOMRectReadOnly,
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const rw = Math.max(triggerRect.width, 0);
  const rh = Math.max(triggerRect.height, 0);
  const insetX = rw > 0 ? Math.min(1, rw) : 0;
  const insetY = rh > 0 ? Math.min(1, rh) : 0;
  const maxX = rw > 0 ? triggerRect.right - insetX : triggerRect.left;
  const maxY = rh > 0 ? triggerRect.bottom - insetY : triggerRect.top;
  const x = Math.min(Math.max(clientX, triggerRect.left), maxX);
  const y = Math.min(Math.max(clientY, triggerRect.top), maxY);
  return { x, y };
}

/** Base del z-index del panel del popup en portal (`computePopupLayout`). */
export const POPUP_PANEL_Z_INDEX_BASE = 1000;

/**
 * z-index recomendado para `Modal` u otros overlays que deben quedar por encima del panel de `Popup`.
 * El panel usa `POPUP_PANEL_Z_INDEX_BASE + (offset % 100)` (típicamente 1000–1099).
 */
export const Z_INDEX_MODAL_ABOVE_POPUP = 1200;

/**
 * z-index del overlay de `Tooltip` / `InlineTooltip`: siempre por debajo del panel de `Popup`
 * (`POPUP_PANEL_Z_INDEX_BASE` + offset % 100, típicamente 1000–1099).
 */
export const Z_INDEX_TOOLTIP = POPUP_PANEL_Z_INDEX_BASE - 100;

/**
 * Con `popoverArrow` manual, `align` debe desplazar el panel respecto al trigger.
 */
export function applyManualPopoverEdgeAlign(
  popoverArrow: PopupArrowMode,
  position: PopupPositionProp,
  left: number,
  top: number,
  triggerLeft: number,
  triggerTop: number,
  triggerWidth: number,
  triggerHeight: number,
  panelWidth: number,
  panelHeight: number,
): { left: number; top: number } {
  if (!popoverArrow || popoverArrow === 'auto') {
    return { left, top };
  }
  const { edge, align } = popoverArrow;

  if (edge === 'right' && position === 'left') {
    if (align === 'center') {
      return { left, top: triggerTop + (triggerHeight - panelHeight) / 2 };
    }
    if (align === 'end') {
      return { left, top: triggerTop + triggerHeight - panelHeight };
    }
    return { left, top: triggerTop };
  }

  if (edge === 'left' && position === 'right') {
    if (align === 'center') {
      return { left, top: triggerTop + (triggerHeight - panelHeight) / 2 };
    }
    if (align === 'end') {
      return { left, top: triggerTop + triggerHeight - panelHeight };
    }
    return { left, top: triggerTop };
  }

  if (
    edge === 'bottom' &&
    (position === 'top' || position === 'top-left' || position === 'top-right')
  ) {
    if (align === 'start') {
      return { left: triggerLeft, top };
    }
    if (align === 'center') {
      return { left: triggerLeft + (triggerWidth - panelWidth) / 2, top };
    }
    if (align === 'end') {
      return { left: triggerLeft + triggerWidth - panelWidth, top };
    }
  }

  if (
    edge === 'top' &&
    (position === 'bottom-left' || position === 'bottom-center' || position === 'bottom-right')
  ) {
    if (align === 'start') {
      return { left: triggerLeft, top };
    }
    if (align === 'center') {
      return { left: triggerLeft + (triggerWidth - panelWidth) / 2, top };
    }
    if (align === 'end') {
      return { left: triggerLeft + triggerWidth - panelWidth, top };
    }
  }

  return { left, top };
}

export type ComputePopupLayoutInput = {
  position: PopupPositionProp;
  offset: number;
  absolute: boolean;
  popoverArrow: PopupArrowMode;
  popupUniqueId: string;
  triggerRect: DOMRectReadOnly;
  popupRect: DOMRectReadOnly;
  /** Solo modo `absolute`: caja del trigger respecto al offset parent. */
  triggerOffsetBox: { top: number; left: number; width: number; height: number };
};

export type ComputePopupLayoutResult = {
  popupStyle: {
    position: 'fixed' | 'absolute';
    top: string;
    left: string;
    zIndex: number;
  };
  arrowPlacement: PopupArrowPlacement | null;
};

/**
 * Calcula top/left del panel, z-index y colocación de flecha (misma semántica que el `usePopup` anterior).
 */
export function computePopupLayout(input: ComputePopupLayoutInput): ComputePopupLayoutResult {
  const {
    position,
    offset,
    absolute,
    popoverArrow,
    popupUniqueId,
    triggerRect,
    popupRect,
    triggerOffsetBox,
  } = input;

  const gapToTrigger = popoverArrow
    ? Math.max(offset, POPOVER_ARROW_MIN_GAP_TO_TRIGGER_PX)
    : offset;
  const arrowOverhang = popoverArrow ? POPUP_ARROW_OVERHANG_PX : 0;

  let top = 0;
  let left = 0;

  if (!absolute) {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const triggerTop = triggerRect.top;
    const triggerLeft = triggerRect.left;
    const triggerWidth = triggerRect.width;
    const triggerHeight = triggerRect.height;

    switch (position) {
      case 'top':
        top = triggerTop - popupRect.height - gapToTrigger - arrowOverhang;
        left = triggerLeft + (triggerWidth - popupRect.width) / 2;
        break;
      case 'top-left':
        top = triggerTop - popupRect.height - gapToTrigger - arrowOverhang;
        left = triggerLeft;
        break;
      case 'top-right':
        top = triggerTop - popupRect.height - gapToTrigger - arrowOverhang;
        left = triggerLeft + triggerWidth - popupRect.width;
        break;
      case 'bottom':
      case 'bottom-center':
        top = triggerTop + triggerHeight + gapToTrigger + arrowOverhang;
        left = triggerLeft + (triggerWidth - popupRect.width) / 2;
        break;
      case 'bottom-left':
        top = triggerTop + triggerHeight + gapToTrigger + arrowOverhang;
        left = triggerLeft;
        break;
      case 'bottom-right':
        top = triggerTop + triggerHeight + gapToTrigger + arrowOverhang;
        left = triggerLeft + triggerWidth - popupRect.width;
        break;
      case 'left':
        top = triggerTop;
        left = triggerLeft - popupRect.width - gapToTrigger - arrowOverhang;
        break;
      case 'right':
        top = triggerTop;
        left = triggerLeft + triggerWidth + gapToTrigger + arrowOverhang;
        break;
    }

    const posFixed = (position ?? 'bottom-right') as PopupPositionProp;
    const aligned = applyManualPopoverEdgeAlign(
      popoverArrow,
      posFixed,
      left,
      top,
      triggerLeft,
      triggerTop,
      triggerWidth,
      triggerHeight,
      popupRect.width,
      popupRect.height,
    );
    left = aligned.left;
    top = aligned.top;

    if (left < 0) left = 0;
    if (left + popupRect.width > windowWidth) left = windowWidth - popupRect.width;
    if (top < 0) top = 0;
    if (top + popupRect.height > windowHeight) top = windowHeight - popupRect.height;

    if (
      (position === 'bottom-right' || position === 'top-right') &&
      popupRect.width > triggerRect.width
    ) {
      left = triggerRect.right - popupRect.width;
      if (left < 0) left = 0;
    }
  } else {
    const relativeTop = triggerOffsetBox.top;
    const relativeLeft = triggerOffsetBox.left;
    const triggerWidth = triggerOffsetBox.width;
    const triggerHeight = triggerOffsetBox.height;

    switch (position) {
      case 'top':
        top = relativeTop - popupRect.height - gapToTrigger - arrowOverhang;
        left = relativeLeft + (triggerWidth - popupRect.width) / 2;
        break;
      case 'top-left':
        top = relativeTop - popupRect.height - gapToTrigger - arrowOverhang;
        left = relativeLeft;
        break;
      case 'top-right':
        top = relativeTop - popupRect.height - gapToTrigger - arrowOverhang;
        left = relativeLeft + triggerWidth - popupRect.width;
        break;
      case 'bottom':
      case 'bottom-center':
        top = relativeTop + triggerHeight + gapToTrigger + arrowOverhang;
        left = relativeLeft + (triggerWidth - popupRect.width) / 2;
        break;
      case 'bottom-left':
        top = relativeTop + triggerHeight + gapToTrigger + arrowOverhang;
        left = relativeLeft;
        break;
      case 'bottom-right':
        top = relativeTop + triggerHeight + gapToTrigger + arrowOverhang;
        left = relativeLeft + triggerWidth - popupRect.width;
        break;
      case 'left':
        top = relativeTop;
        left = relativeLeft - popupRect.width - gapToTrigger - arrowOverhang;
        break;
      case 'right':
        top = relativeTop;
        left = relativeLeft + triggerWidth + gapToTrigger + arrowOverhang;
        break;
    }

    const posAbs = (position ?? 'bottom-right') as PopupPositionProp;
    const alignedAbs = applyManualPopoverEdgeAlign(
      popoverArrow,
      posAbs,
      left,
      top,
      relativeLeft,
      relativeTop,
      triggerWidth,
      triggerHeight,
      popupRect.width,
      popupRect.height,
    );
    left = alignedAbs.left;
    top = alignedAbs.top;
  }

  const zIndexOffset = parseInt(popupUniqueId.replace(/\D/g, ''), 10) || 0;
  const uniqueZIndex = POPUP_PANEL_Z_INDEX_BASE + (zIndexOffset % 100);

  const popupStyle = {
    position: absolute ? ('absolute' as const) : ('fixed' as const),
    top: `${top}px`,
    left: `${left}px`,
    zIndex: uniqueZIndex,
  };

  const placement = resolvePopupArrowPlacement(
    popoverArrow,
    position,
    triggerRect as DOMRect,
    left,
    top,
    popupRect.width,
    popupRect.height,
    absolute,
  );

  return { popupStyle, arrowPlacement: placement };
}

/** Ventana, visualViewport y ancestros con overflow desplazable (scroll no sube al window). */
export function collectScrollRepositionTargets(trigger: HTMLElement | null): EventTarget[] {
  const targets = new Set<EventTarget>();
  targets.add(window);
  if (typeof window !== 'undefined' && window.visualViewport) {
    targets.add(window.visualViewport);
  }
  let el: HTMLElement | null = trigger;
  while (el) {
    const st = window.getComputedStyle(el);
    if (/(auto|scroll|overlay)/.test(`${st.overflow}${st.overflowY}${st.overflowX}`)) {
      targets.add(el);
    }
    el = el.parentElement;
  }
  return [...targets];
}
